/**
 * Modality Subscription Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { subscriptionService } from './subscription.service'

const QUERY_KEYS = {
  mySubscriptions: 'my-subscriptions',
  userSubscriptions: (userId: string) => ['user-subscriptions', userId],
  subscriptionSummary: 'subscription-summary',
  modalitySubscribers: (modalityId: string) => ['modality-subscribers', modalityId],
}

/**
 * Hook for getting current user's subscriptions
 */
export function useMySubscriptions() {
  return useQuery({
    queryKey: [QUERY_KEYS.mySubscriptions],
    queryFn: () => subscriptionService.getMySubscriptions(),
  })
}

/**
 * Hook for getting user's subscriptions (admin)
 */
export function useUserSubscriptions(userId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.userSubscriptions(userId),
    queryFn: () => subscriptionService.getUserSubscriptions(userId),
    enabled: !!userId,
  })
}

/**
 * Hook for getting subscription summary (admin)
 */
export function useSubscriptionSummary() {
  return useQuery({
    queryKey: [QUERY_KEYS.subscriptionSummary],
    queryFn: () => subscriptionService.getSummary(),
  })
}

/**
 * Hook for getting modality subscribers
 */
export function useModalitySubscribers(modalityId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.modalitySubscribers(modalityId),
    queryFn: () => subscriptionService.getSubscribers(modalityId),
    enabled: !!modalityId,
  })
}

/**
 * Hook for subscribing user to modality (admin)
 */
export function useSubscribeToModality() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, modalityId }: { userId: string; modalityId: string }) =>
      subscriptionService.subscribe(userId, modalityId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.userSubscriptions(variables.userId),
      })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.subscriptionSummary] })
    },
  })
}

/**
 * Hook for unsubscribing user from modality (admin)
 */
export function useUnsubscribeFromModality() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, modalityId }: { userId: string; modalityId: string }) =>
      subscriptionService.unsubscribe(userId, modalityId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.userSubscriptions(variables.userId),
      })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.subscriptionSummary] })
    },
  })
}

/**
 * Hook for updating all subscriptions for a user (bulk) (admin)
 */
export function useUpdateUserSubscriptions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, modalityIds }: { userId: string; modalityIds: string[] }) =>
      subscriptionService.updateUserSubscriptions(userId, modalityIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.userSubscriptions(variables.userId),
      })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.subscriptionSummary] })
    },
  })
}

export { subscriptionService }
